import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Button, Group, Stack } from '@mantine/core';
import {
  IconBold,
  IconItalic,
  IconList,
  IconListNumbers,
  IconLink,
  IconPhoto,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  label?: string;
}

export function RichTextEditor({ content, onChange, label }: RichTextEditorProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'ProseMirror',
      },
    },
  });

  useEffect(() => {
    if (editor && mounted && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor, mounted]);

  if (!mounted || !editor) {
    return (
      <Stack gap="xs">
        {label && <label style={{ fontWeight: 500 }}>{label}</label>}
        <div
          style={{
            border: '1px solid var(--mantine-color-gray-4)',
            borderRadius: 'var(--mantine-radius-md)',
            padding: '12px',
            minHeight: '200px',
          }}
        >
          <div style={{ color: 'var(--mantine-color-dimmed)' }}>Loading editor...</div>
        </div>
      </Stack>
    );
  }

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <Stack gap="xs">
      {label && <label style={{ fontWeight: 500 }}>{label}</label>}
      <div
        style={{
          border: '1px solid var(--mantine-color-gray-4)',
          borderRadius: 'var(--mantine-radius-md)',
          padding: '8px',
        }}
      >
        <Group gap="xs" mb="xs" wrap="nowrap">
          <Button
            variant={editor.isActive('bold') ? 'filled' : 'subtle'}
            size="xs"
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <IconBold size={16} />
          </Button>
          <Button
            variant={editor.isActive('italic') ? 'filled' : 'subtle'}
            size="xs"
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <IconItalic size={16} />
          </Button>
          <Button
            variant={editor.isActive('bulletList') ? 'filled' : 'subtle'}
            size="xs"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <IconList size={16} />
          </Button>
          <Button
            variant={editor.isActive('orderedList') ? 'filled' : 'subtle'}
            size="xs"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <IconListNumbers size={16} />
          </Button>
          <Button variant="subtle" size="xs" onClick={addLink}>
            <IconLink size={16} />
          </Button>
          <Button variant="subtle" size="xs" onClick={addImage}>
            <IconPhoto size={16} />
          </Button>
        </Group>
        <div
          style={{
            minHeight: '200px',
            padding: '12px',
            outline: 'none',
          }}
        >
          <EditorContent editor={editor} />
        </div>
      </div>
    </Stack>
  );
}

